import React from 'react'

export type Theme = 'light' | 'dark'

const STORAGE_KEY = 'lm-theme'

interface ThemeContextValue {
  theme: Theme
  setTheme: (t: Theme) => void
  toggle: () => void
}

const ThemeContext = React.createContext<ThemeContextValue | null>(null)

function storedTheme(): Theme | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY)
    return v === 'light' || v === 'dark' ? v : null
  } catch {
    return null
  }
}

/**
 * Light/dark for the whole app. LIGHT is the default: a first load with no
 * saved choice starts light regardless of the OS setting. The toggle persists
 * the user's choice in localStorage.
 *
 * index.html applies the same logic inline before first paint — change one,
 * change the other.
 */
export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [explicit, setExplicit] = React.useState<Theme | null>(() => storedTheme())

  const theme: Theme = explicit ?? 'light'

  React.useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
    document.documentElement.style.colorScheme = theme
  }, [theme])

  const setTheme = React.useCallback((t: Theme) => {
    setExplicit(t)
    try {
      localStorage.setItem(STORAGE_KEY, t)
    } catch {
      /* private mode — the choice just won't survive a reload */
    }
  }, [])

  const value = React.useMemo<ThemeContextValue>(
    () => ({
      theme,
      setTheme,
      toggle: () => setTheme(theme === 'light' ? 'dark' : 'light'),
    }),
    [theme, setTheme],
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme(): ThemeContextValue {
  const ctx = React.useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>')
  return ctx
}
